import { DisplayMarketAsset, getPointsLabel, LINKS } from "@silo/types";
import { YieldMetrics } from "./utils";
import { cn } from "../../utils/class-names";
import { getDebtRewardsStateColor } from "../apr-display-v2/utils";
import { BreakdownDebt } from "../apr-display-v2/breakdown/breakdown-debt";
import { NetYieldMessage } from "../apr-display-v2";
import { Perk } from "../perk";
import { ExternalLink } from "../link";
import { PointsLinks } from "../perk/points-links";
import { asPercent } from "@silo/utils";

interface DebtAprContentProps {
  asset: DisplayMarketAsset;
  yieldMetrics: YieldMetrics;
}

export function DebtAprContent({
  asset,
  yieldMetrics,
}: DebtAprContentProps): JSX.Element {
  const { symbol, isDebtAprFixed, debtPoints, debtMerklRewards, debtBaseApr } =
    asset;
  const {
    debtRewardsState,
    hasDebtRewards,
    debtRewards,
    showFixedLabel,
    totalDebtApr,
  } = yieldMetrics;

  const hasDebtSuperFest = debtMerklRewards.some(
    (reward) => reward.isSuperFest
  );

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-2.5">
        <div className="font-medium text-white">{symbol} total borrow APR</div>

        <div
          className={cn(
            "flex flex-col font-medium font-mono text-2xl",
            isDebtAprFixed ? "text-amber-300" : "text-white",
            getDebtRewardsStateColor(debtRewardsState)
          )}
        >
          <div>{asPercent(totalDebtApr)}</div>

          {showFixedLabel && <div className="font-sans">Fixed</div>}
        </div>
      </div>

      {hasDebtRewards && (
        <div className="flex flex-col gap-5">
          <BreakdownDebt
            debtBaseApr={debtBaseApr}
            debtRewards={debtRewards}
            align="left"
            isDebtAprFixed={isDebtAprFixed}
          />

          {debtRewardsState === "netYield" && <NetYieldMessage />}

          <ExternalLink size="small" href={LINKS.SILOPEDIA.INCENTIVES}>
            Rewards details
          </ExternalLink>

          {hasDebtSuperFest && (
            <ExternalLink size="small" href={LINKS.SUPERFEST}>
              SuperFest details
            </ExternalLink>
          )}
        </div>
      )}

      {debtPoints.length > 0 && (
        <div className="flex flex-col gap-5">
          <Perk.Root>
            <Perk.Label />

            {debtPoints.map((pointsItem) => (
              <Perk.Item>
                <Perk.PointsIcon
                  pointsKey={pointsItem._tag}
                  className={Perk.iconClassName}
                />

                <Perk.Emphasis>{getPointsLabel(pointsItem)}</Perk.Emphasis>

                <Perk.PointsLabel />
              </Perk.Item>
            ))}
          </Perk.Root>

          <PointsLinks pointsItems={debtPoints} />
        </div>
      )}
    </div>
  );
}
